/**
 * Spotlight Page Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive home/spotlight page with advanced features:
 * - Featured news and events showcase
 * - Interactive Daire AI chat integration
 * - Member benefits visualization with PDF access
 * - Irish Funds history timeline display
 * - Live ticker feed for interesting facts
 * - Modal system for PDFs and information display
 * - Date-based filtering for featured content
 * - Responsive card layouts and animations
 * 
 * Features:
 * - Featured news filtering by date ranges
 * - Interactive timeline with historical milestones
 * - Multi-modal PDF viewer with positioning
 * - Daire AI chat with learn more functionality
 * - Member benefits cards with year-based PDFs
 * - Ticker feed with rotating content
 * - Navigation integration to news room
 * - Reveal animations on scroll
 * - Repository items integration
 * 
 * @returns {JSX.Element} Spotlight page component with comprehensive home content
 */
import React, { useState, useEffect } from 'react';
import DaireChat from '../components/Daire/DaireChat';
import Divider from '../components/Common/Divider';
import UnifiedCards from '../components/Common/UnifiedCards';
import Timeline from '../components/Home/Timeline';
import TickerFeed from '../components/Home/Ticker';
import apiHelper from '../utils/apiHelper';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import UnifiedNews from '../components/NewsRoom/UnifiedNews';
import InfoModal from '../modals/InfoModal';
import PDFModal from '../modals/PDFModal';
import NewsTableModal from '../modals/NewsTableModal';
import { useNavigate } from 'react-router-dom';
import { prefetchAll } from '../utils/prefetchUtils';

import '../css/Colors.css';
import './Spotlight.css';

const { FILE_NAMES } = require('../utils/constants');

/**
 * Spotlight component for home page content
 * with featured news, AI chat, and member benefits
 */
function Spotlight() {
    const [benefitsData, setBenefitsData] = useState([]);
    const [featuredNewsData, setFeaturedNewsData] = useState([]);
    const [tickerData, setTickerData] = useState([]);
    const [timelineData, setTimelineData] = useState([]);
    const [daireIntroData, setDaireIntroData] = useState('');
    const [showDaireModal, setShowDaireModal] = useState(false);
    const [showFeaturedNewsModal, setShowFeaturedNewsModal] = useState(false);
    const [repositoryItemsData, setRepositoryItemsData] = useState([]);
    const [modals, setModals] = useState([]);

    const navigate = useNavigate();

    const sortByPortalPublishDate = (data) => {
        return data.sort((a, b) => {
            // Add error handling for missing or invalid dates
            if (!a.portalPublishDate || !b.portalPublishDate) {
                return 0;
            }

            try {
                // Handle YYYY-MM-DD format (featured news)
                if (a.portalPublishDate.includes('-')) {
                    const dateA = new Date(a.portalPublishDate);
                    const dateB = new Date(b.portalPublishDate);
                    
                    // Validate the created dates
                    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
                        return 0;
                    }
                    
                    return dateB - dateA; // Sort descending (newest first)
                } else {
                    // Handle DD/MM/YYYY format (weekly news)
                    const [dayA, monthA, yearA] = a.portalPublishDate.split('/').map(Number);
                    const [dayB, monthB, yearB] = b.portalPublishDate.split('/').map(Number);
                    
                    // Validate the parsed values
                    if (isNaN(dayA) || isNaN(monthA) || isNaN(yearA) || isNaN(dayB) || isNaN(monthB) || isNaN(yearB)) {
                        return 0;
                    }
                    
                    const dateA = new Date(yearA, monthA - 1, dayA);
                    const dateB = new Date(yearB, monthB - 1, dayB);
                    
                    // Validate the created dates
                    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
                        return 0;
                    }
                    
                    return dateB - dateA; // Sort descending (newest first)
                }
            } catch (error) {
                return 0;
            }
        });
    };

    const goToNewsRoom = () => {
        navigate('/newsroom');
    };

    const openPDFModal = (id, title, url) => {
        setModals((prevModals) => {
            const existingModalIndex = prevModals.findIndex((modal) => modal.id === id);
            if (existingModalIndex !== -1) {
                // Bring to front
                const updatedModals = [...prevModals];
                const maxZIndex = Math.max(...prevModals.map((modal) => modal.zIndex)) + 1;
                updatedModals[existingModalIndex].isOpen = true;
                updatedModals[existingModalIndex].zIndex = maxZIndex;
                return updatedModals;
            } else {
                // Open new
                const maxZIndex = Math.max(...prevModals.map((modal) => modal.zIndex), 0) + 1;
                return [
                    ...prevModals,
                    {
                        id,
                        title,
                        url,
                        isOpen: true,
                        top: `${5 + (prevModals.length * 2)}%`,
                        left: `${10 + (prevModals.length * 2)}%`,
                        zIndex: maxZIndex,
                    },
                ];
            }
        });
    };

    const closePDFModal = (id) => {
        setModals((prevModals) =>
            prevModals.map((modal) =>
                modal.id === id ? { ...modal, isOpen: false } : modal
            )
        );
    };

    useEffect(() => {
        const observerOptions = {
            threshold: 0.15,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('active');
                }
            });
        }, observerOptions);

        const revealElements = document.querySelectorAll('.reveal');
        revealElements.forEach(element => observer.observe(element));

        const fetchFileInfoAndContent = async () => {
            try {
                // Featured News
                const featuredNewsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.FEATURED_NEWS);
                const currentDate = new Date();

                // Filter featured news based on dates only
                const filteredFeaturedNews = featuredNewsResult.fileContent.filter(article => {
                    // If it's featured indefinitely, always show it
                    if (article.featuredIndefinitely) return true;

                    // Check date range if specified
                    if (article.featuredStartDate && article.featuredEndDate) {
                        const startDate = new Date(article.featuredStartDate);
                        const endDate = new Date(article.featuredEndDate);
                        return currentDate >= startDate && currentDate <= endDate;
                    }

                    // If no dates specified, show it
                    return true;
                });

                

                const sortedFeaturedNews = sortByPortalPublishDate(filteredFeaturedNews);
                setFeaturedNewsData(sortedFeaturedNews);
                //Daire Learn More Window content
                const daireOptionsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.DAIRE_OPTIONS);
                const daireIntroContent = daireOptionsResult.fileContent.find(option => option.title === 'Disclaimer3');
                
                setDaireIntroData(daireIntroContent ? daireIntroContent.value : 'No content available');
                // Benefits
                const benefitsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.BENEFITS);
                setBenefitsData(benefitsResult.fileContent);
                // Repository Items
                const repositoryItemsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.REPOSITORY_ITEMS);
                setRepositoryItemsData(repositoryItemsResult.fileContent);
                
                // Timeline
                const timelineResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.TIMELINE);
                setTimelineData(timelineResult.fileContent);
                // Ticker
                const tickerResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.TICKER);
                setTickerData(tickerResult.fileContent);
                
                // Prefetch all content for better performance
                prefetchAll(5, 5, 10);
            } catch (err) {
                console.error('Error fetching data:', err);
            }
        };

        fetchFileInfoAndContent();

        return () => {
            revealElements.forEach(element => observer.unobserve(element));
        };
    }, []);

    return (
        <div className='totalPage'>
            <section className="wrapper">
                <div id="featurednews" className="container mt-10 pb-6 reveal">
                    <h2 className="text-uppercase main-header">Featured News and Events</h2>
                </div>
                <div className="container reveal reveal-delay-1">
                    {featuredNewsData.length > 0 ? (
                        <>
                            <UnifiedNews data={featuredNewsData} autoPlay={true} showTags={true} />
                            <div className='align-right newsRoomLink'>
                                <button 
                                    className="btn btn-compact mr-3" 
                                    onClick={() => setShowFeaturedNewsModal(true)}
                                    title="Discover Featured News & Events"
                                    style={{ 
                                        background: 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        border: 'none',
                                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                                        transition: 'all 0.3s ease',
                                        transform: 'translateY(0px)',
                                        textTransform: 'none'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = 'linear-gradient(45deg, #764ba2 0%, #667eea 100%)';
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'linear-gradient(45deg, #667eea 0%, #764ba2 100%)';
                                        e.target.style.transform = 'translateY(0px)';
                                        e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                                    }}
                                >
                                    <i className="fas fa-compass"></i> More...
                                </button>
                                <button className='btn btn-primary btn-compact' onClick={goToNewsRoom}>
                                    <i className="fas fa-newspaper"></i> News Room
                                </button>
                            </div>
                        </>
                    ) : (
                        <LoadingSpinner />
                    )}
                </div>
            </section>

            <Divider height={60} />
            <section id="daire" className="wrapper bg-gradient-primary">
                <div className="container reveal">
                    <div className="daire_container home_daire_container">
                        <div className="daire-intro">
                            <div className="daire-avatar">
                                <img src="/assets/img/daire/daire-avatar-nobg.png" alt="Daire Avatar" className="background-image" />
                                <button 
                                    className="daire-info-btn" 
                                    onClick={() => setShowDaireModal(true)} 
                                    aria-label="Daire Usage Policy"
                                    title="Daire Usage Policy"
                                >
                                    <i className="fa-solid fa-circle-info"></i>
                                </button>
                            </div>
                        </div>
                        <div className="daire-chatbox">
                            <DaireChat hideFullscreen={true} isOnDairePage={true} />
                        </div>
                    </div>
                </div>
            </section>

            <Divider height={80} />
            <section id="benefits" className="wrapper bg-gradient-primary">
                <div className="container pb-10 reveal">
                    <div className="benefits-header">
                        <h2 className="text-uppercase main-header">Member Benefits</h2>
                        <div className="years-container">
                            {repositoryItemsData
                                .filter(item => item.mpkey === "Member_Benefit")
                                .sort((a, b) => Number(b.Year) - Number(a.Year))
                                .map(item => (
                                    <span
                                        key={item.id}
                                        className="year-badge btn-compact"
                                        onClick={() => openPDFModal(item.id, item.title, item.doc)}
                                        style={{ cursor: 'pointer' }}
                                        title="Open PDF"
                                    >
                                        <i className="fa-solid fa-file-pdf small-pdf-icon" aria-label="PDF"></i>
                                        {item.Year}
                                    </span>
                                ))}
                        </div>
                    </div>
                </div>
                <div id='MemberBenifitsGrid' className="container reveal reveal-delay-1">
                    {benefitsData.length > 0 ? (
                        <UnifiedCards data={benefitsData} showImages={true} />
                    ) : (
                        <LoadingSpinner text="Loading Member Benefits" />
                    )}
                </div>
            </section>

            <Divider height={80} />
            <section id="evolution" className="wrapper bg-gradient-primary">
                <div className="container pb-10 reveal">
                    <h2 className="text-uppercase main-header">History & Evolution of Irish Funds</h2>
                </div>
                <div className="container reveal reveal-delay-1">
                    {timelineData.length > 0 ? (
                        <Timeline data={timelineData} />
                    ) : (
                        <LoadingSpinner text="Loading Timeline" />
                    )}
                </div>
            </section>

            <Divider height={80} />
            <section id="ticker" className="wrapper bg-gradient-primary">
                <div className="container pb-12">
                    <div className="text-center reveal">
                        <div id="InterestingTicker">
                            {tickerData.length > 0 ? (
                                <TickerFeed data={tickerData} />
                            ) : (
                                <LoadingSpinner text="Loading Ticker" />
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {showDaireModal && (
                <InfoModal
                    title="Daire Usage Policy"
                    data={daireIntroData}
                    show={showDaireModal}
                    onClose={() => setShowDaireModal(false)}
                    small={true}
                />
            )}

            {modals.map((modal) =>
                modal.isOpen && (
                    <PDFModal
                        key={modal.id}
                        title={modal.title}
                        url={modal.url}
                        show={modal.isOpen}
                        onClose={() => closePDFModal(modal.id)}
                        allowFullscreen={true}
                        allowMove={true}
                        showLinkinToolBar={true}
                        style={{ top: modal.top, left: modal.left, zIndex: modal.zIndex }}
                        listItemId={modal.id}
                    />
                )
            )}

            {showFeaturedNewsModal && (
                <NewsTableModal
                    show={showFeaturedNewsModal}
                    onClose={() => setShowFeaturedNewsModal(false)}
                    data={featuredNewsData}
                    title="Featured News & Events"
                />
            )}
        </div>
    );
}

export default Spotlight;